<script setup lang="ts">
import { ref, onMounted, watch, inject, type Ref, nextTick } from "vue";
import { gsap } from "gsap";
import { Flip } from "gsap/Flip";
import type { IOutlineTabItem, IOutlineTabsProps } from "./OutlineTabs.models";

gsap.registerPlugin(Flip);

const props = withDefaults(defineProps<IOutlineTabsProps>(), {
  items: () => new Array<IOutlineTabItem>(),
  currentPath: "",
  defaultIndex: 0
});

const emit = defineEmits<{
  (e: "navigate", path: string): void;
}>();

const getIndexFromPath = (path: string): number => {
  const normalized = path.replace(/\/$/, "");
  return props.items.findIndex((item) => {
    const itemPath = item.path.replace(/\/$/, "");
    return normalized === itemPath || normalized.endsWith(itemPath);
  });
};

const resolveIndex = (path: string): number => {
  const index = getIndexFromPath(path);
  return index >= 0 ? index : props.defaultIndex;
};

const activeIndex = ref(resolveIndex(props.currentPath));
const itemEls = ref<HTMLElement[]>([]);
const activeBgEl = ref<HTMLElement | null>(null);
const opacityActiveBg = ref(0);
const isFlipping = ref(false);

const getDuration = (fromIndex: number, toIndex: number): number => {
  const distance = Math.abs(fromIndex - toIndex);
  return Math.max(Math.min(0.1 * distance, 0.5), 0.2);
};

const moveActiveBg = (targetIndex: number) => {
  const el = itemEls.value[targetIndex];
  if (activeBgEl.value && el) {
    el.insertBefore(activeBgEl.value, el.firstChild);
  }
};

const flipActiveBgAnimation = (newIndex: number) => {
  if (isFlipping.value) return;
  isFlipping.value = true;

  const state = Flip.getState(activeBgEl.value);
  const previousIndex = activeIndex.value;
  activeIndex.value = newIndex;
  moveActiveBg(newIndex);

  Flip.from(state, {
    duration: getDuration(previousIndex, newIndex),
    ease: "power2.inOut",
    scale: true,
    absolute: false,
    transformOrigin: "left top",
    onComplete: () => {
      isFlipping.value = false;
    }
  });
};

const getItemClasses = (base: string, index: number) => [
  base,
  { [`${base}--active`]: index === activeIndex.value }
];

const itemOnClick = (event: Event, item: IOutlineTabItem, index: number) => {
  event.preventDefault();
  if (index === activeIndex.value) return;
  flipActiveBgAnimation(index);
  emit("navigate", item.path);
};

const focusTab = (index: number) => {
  const tabEls = itemEls.value;
  const link = tabEls[index]?.querySelector("a") as HTMLElement | null;
  link?.focus();
};

const onTabKeydown = (event: KeyboardEvent, currentIndex: number) => {
  let targetIndex: number | null = null;

  switch (event.key) {
    case "ArrowRight":
    case "ArrowDown":
      event.preventDefault();
      targetIndex = (currentIndex + 1) % props.items.length;
      break;
    case "ArrowLeft":
    case "ArrowUp":
      event.preventDefault();
      targetIndex =
        (currentIndex - 1 + props.items.length) % props.items.length;
      break;
    case "Home":
      event.preventDefault();
      targetIndex = 0;
      break;
    case "End":
      event.preventDefault();
      targetIndex = props.items.length - 1;
      break;
  }

  if (targetIndex !== null) {
    focusTab(targetIndex);
  }
};

watch(
  () => props.currentPath,
  (path) => {
    const index = getIndexFromPath(path);
    if (index >= 0 && index !== activeIndex.value) {
      flipActiveBgAnimation(index);
    }
  }
);

onMounted(() => {
  moveActiveBg(activeIndex.value);
  nextTick(() => {
    opacityActiveBg.value = 1;
  });
});
</script>
<template>
  <div class="OutlineTabs">
    <p class="OutlineTabs__title">
      <span
        class="OutlineTabs__icon"
        aria-hidden="true"></span
      >Outline
    </p>
    <nav
      class="OutlineTabs__nav"
      aria-label="Research topic sections">
      <ul
        class="OutlineTabs__list"
        role="tablist">
        <li
          v-for="(item, index) in items"
          ref="itemEls"
          :key="item.path"
          role="presentation"
          :class="getItemClasses('OutlineTabs__item', index)">
          <a
            :href="item.path"
            role="tab"
            :aria-selected="index === activeIndex"
            :tabindex="index === activeIndex ? 0 : -1"
            :class="getItemClasses('OutlineTabs__link', index)"
            :data-event="
              $ga.getDataEventValue('OutlineTabsItem', 'a', item.path)
            "
            @click="itemOnClick($event, item, index)"
            @keydown="onTabKeydown($event, index)">
            <span aria-hidden="true"></span>
            <p class="OutlineTabs__link__text">{{ item.text }}</p>
          </a>
        </li>
      </ul>
      <div
        ref="activeBgEl"
        class="OutlineTabs__activeBg"
        :style="{ opacity: opacityActiveBg }"></div>
    </nav>
  </div>
</template>
<style lang="scss">
@mixin activeState {
  background: $white;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  z-index: 0;

  &::before {
    content: "";
    position: absolute;
    left: -4px;
    top: 0;
    width: 4px;
    height: 100%;
    border-radius: 16px;
    background: var(--maincolor50);
  }
}

.OutlineTabs {
  border: 1px solid $grey40;
  border-radius: 16px;
  width: 100%;
  padding: 16px 24px;
  box-sizing: border-box;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: calc(var(--ibar-mix-height) + spacing());

  @media #{$break-md} {
    border: none;
  }

  &__title {
    @include f("fluid-heading-xs");
    font-weight: 500;
    color: $textPrimary;
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  &__nav {
    flex: 1;
    overflow: auto;
  }

  &__list {
    padding-left: 4px;
  }

  &__icon {
    margin-right: 8px;

    &::before {
      content: "\e94b";
      width: 16px;
      height: 16px;
      font-size: 16px;
      font-weight: 300;
      font-family: $fontIcon;
    }
  }

  &__item {
    position: relative;
    z-index: 1;

    &--active {
      z-index: 0;
    }

    &:not(.OutlineTabs__item--active):hover {
      .OutlineTabs {
        &__link {
          color: $textPrimary;
          background: $backgroundTertiary;
          border-radius: 4px;

          &__text {
            margin-right: 0;
          }

          span {
            transition: width 0.16s $bezier;
            width: 16px;
            flex-shrink: 0;
            align-self: baseline;
            margin-right: 3px;
            margin-top: 2px;

            &::before {
              left: 0;
            }
          }
        }
      }
    }
  }

  &__link {
    @include f-16-300;
    display: flex;
    align-items: center;
    color: $textTertiary;
    padding: 8px;
    transition:
      color 0.16s $bezier,
      background 0.16s $bezier,
      width 0.16s $bezier;
    position: relative;
    z-index: 1;
    will-change: transform;

    &__text {
      flex: 1;
      overflow: hidden;
      width: calc(100%);
      will-change: padding, width;
      margin-right: 19px;
      transition: margin-right 0.16s $bezier;
    }

    span {
      transition: width 0.16s $bezier;
      width: 0;
      height: 16px;
      position: relative;
      overflow: hidden;
      will-change: width;

      &::before {
        content: "\e979";
        transform: rotate(90deg);
        width: 16px;
        height: 16px;
        position: absolute;
        top: 0;
        left: -16px;
        font-size: 16px;
        font-weight: 300;
        font-family: $fontIcon;
        color: var(--maincolor50);
        transition: left 0.16s $bezier;
      }
    }

    &--active {
      color: $textPrimary;

      @media #{$break-md} {
        @include activeState;
      }
    }
  }

  &__activeBg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    @include activeState;

    @media #{$break-md} {
      display: none;
    }
  }
}
</style>
